/**
 * Route configuration with paths and roles.
 * This file is extracted from Routes.tsx to break circular dependencies.
 * Components like DisabledRouteEvaluator can import from here instead of Routes.tsx.
 */

import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';

export type RouteConfig = {
    path?: string;
    roles?: Roles[];
    children?: RouteConfig[];
};

/**
 * Route configuration without JSX elements.
 * Used for route access checking and role-based filtering.
 */
export const routeConfigs: RouteConfig[] = [
    {
        path: Routes.Tenant,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            { path: Routes.Users },
            { path: Routes.Licenses },
            { path: Routes.UserGroups },
            {
                path: Routes.Roles,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ]
            },
            { path: Routes.Claims },
            {
                path: Routes.Acls,
                children: [
                    { path: Routes.AclResources },
                    {
                        path: Routes.AclAccesses,
                        roles: [Roles.Admin, Roles.AppManager]
                    }
                ]
            },
            { path: Routes.AuthorizedIssuers }
        ]
    },
    {
        path: Routes.ApplicationSuite,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Tenants,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.ApiKeys,
                roles: [Roles.Admin, Roles.AppManager]
            }
        ]
    },
    {
        path: Routes.Role,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.UserGroups,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Claims,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.Claim,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Roles,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.UserGroup,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            {
                path: Routes.Users,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Roles,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            { path: Routes.AclEntries }
        ]
    },
    {
        path: Routes.User,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
            Roles.GlobalRoleManager
        ],
        children: [
            {
                path: Routes.Roles,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager
                ]
            },
            {
                path: Routes.UserGroups,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.Claims,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            },
            {
                path: Routes.AclEntries,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.License,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
        children: [
            { path: Routes.Users },
            { path: Routes.UserGroups },
            {
                path: Routes.AclEntries,
                roles: [
                    Roles.Admin,
                    Roles.AppManager,
                    Roles.MultiTenantRO,
                    Roles.UserManager,
                ],
            }
        ]
    },
    {
        path: Routes.UserGroupsReport,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.UserGroupACLsReport,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.LicensesReport,
        roles: [
            Roles.Admin,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.UsersWithNewAccessReport,
        roles: [
            Roles.Admin,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.AclResourceUserListReport,
        roles: [
            Roles.Admin,
            Roles.UserManager,
            Roles.AppManager,
        ],
    },
    {
        path: Routes.ApplicationSuites,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager
        ],
    },
    {
        path: Routes.Users,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.UsersDetails,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
            Roles.GlobalRoleManager
        ],
    },
    {
        path: Routes.Reports,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Monitoring,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Admin,
        roles: [
            Roles.Admin,
            Roles.MultiTenantRO,
            Roles.UserManager
        ],
    },
    {
        path: Routes.AccountLinkingDashboard,
        roles: [
            Roles.Admin,
            Roles.MultiTenantRO,
            Roles.UserManager
        ]
    },
    {
        path: Routes.Imports,
        roles: [
            Roles.Admin,
            Roles.UserManager
        ]
    },
    {
        path: Routes.GlobalRoles,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Help,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    {
        path: Routes.Home,
        roles: [
            Roles.Admin,
            Roles.AppManager,
            Roles.GlobalRoleManager,
            Roles.MultiTenantRO,
            Roles.UserManager,
        ],
    },
    { path: '*' }
];
